import { forwardRef } from 'react';
import {
  FormControlLabel,
  FormControlLabelProps,
  FormHelperText,
  Checkbox as MuiCheckbox,
  CheckboxProps as MuiCheckboxProps,
} from '@mui/material';

/**
 * Props for the Checkbox component.
 * Extends FormControlLabelProps but overrides onChange behavior to simplify form handling.
 */
export interface CheckboxProps
  extends Omit<FormControlLabelProps, 'control' | 'checked' | 'onChange'> {
  /**
   * Label text for the checkbox.
   * Required for accessibility and clarity.
   */
  label: string;
  /** Whether the checkbox is checked */
  checked?: boolean;
  /**
   * Callback fired when the checkbox state changes.
   * Receives a boolean value (not the synthetic event) for simpler form handling.
   *
   * @param checked - The new checked state
   */
  onChange?: (checked: boolean) => void;
  /** Whether the checkbox is disabled */
  disabled?: boolean;
  /**
   * If true, the helper text will be displayed in error color.
   * Typically used alongside helperText to show validation errors.
   */
  error?: boolean;
  /**
   * Helper text to display below the checkbox.
   * Can be used for additional context or validation error messages.
   */
  helperText?: string;
  /**
   * Additional props to pass to the underlying MUI Checkbox component.
   * Useful for advanced customization like color, size, indeterminate state, etc.
   */
  checkboxProps?: MuiCheckboxProps;
  /**
   * Test identifier for automated testing.
   * Applied to the FormControlLabel wrapper.
   */
  'data-testid'?: string;
  /**
   * Data identifier attribute for tracking or identification.
   * Applied to the FormControlLabel wrapper.
   */
  'data-identifier'?: string;
}

/**
 * A checkbox component with integrated label and helper text support.
 * Wraps Material UI's FormControlLabel and Checkbox for a complete form field.
 *
 * This component simplifies checkbox usage by:
 * - Providing built-in label and helper text support
 * - Simplifying onChange to receive boolean instead of event
 * - Supporting error states with styled helper text
 * - Maintaining full MUI Checkbox customization via checkboxProps
 *
 * @example
 * Basic usage with label:
 * ```tsx
 * <Checkbox
 *   label="Accept terms and conditions"
 *   checked={accepted}
 *   onChange={(checked) => setAccepted(checked)}
 * />
 * ```
 *
 * @example
 * With helper text and error state:
 * ```tsx
 * <Checkbox
 *   label="I agree to the terms"
 *   checked={agreed}
 *   onChange={(checked) => setAgreed(checked)}
 *   helperText="You must accept to continue"
 *   error={!agreed && submitted}
 * />
 * ```
 *
 * @example
 * Without visible label (provide aria-label for accessibility):
 * ```tsx
 * <Checkbox
 *   checked={selected}
 *   onChange={(checked) => setSelected(checked)}
 *   checkboxProps={{ 'aria-label': 'Select item' }}
 * />
 * ```
 *
 * @example
 * With advanced MUI Checkbox customization:
 * ```tsx
 * <Checkbox
 *   label="Select all"
 *   checked={allSelected}
 *   onChange={(checked) => setAllSelected(checked)}
 *   checkboxProps={{
 *     indeterminate: someSelected && !allSelected,
 *     color: 'secondary',
 *     size: 'small'
 *   }}
 * />
 * ```
 */
export const Checkbox = forwardRef<HTMLLabelElement, CheckboxProps>(
  (
    {
      label,
      checked,
      onChange,
      disabled,
      error,
      helperText,
      checkboxProps,
      'data-testid': dataTestId,
      'data-identifier': dataIdentifier,
      ...formControlLabelProps
    },
    ref
  ) => {
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event.target.checked);
    };

    return (
      <>
        <FormControlLabel
          ref={ref}
          control={
            <MuiCheckbox
              checked={checked}
              onChange={handleChange}
              disabled={disabled}
              {...checkboxProps}
            />
          }
          label={label}
          disabled={disabled}
          data-testid={dataTestId}
          data-identifier={dataIdentifier}
          {...formControlLabelProps}
        />
        {helperText && (
          <FormHelperText error={error} sx={{ ml: 4 }}>
            {helperText}
          </FormHelperText>
        )}
      </>
    );
  }
);

Checkbox.displayName = 'Checkbox';
